import React, { useState } from "react";

/**
 * MediaSettings
 *
 * Configures various media-related settings, such as:
 * - Music upload options (file size limits, accepted formats)
 * - Extended time fee rules
 * - Photo & video upload toggles (photogenic, modeling, event videos)
 * - Critique recording or remote judging
 */
const MediaSettings = () => {
  // Example local states for storing media config
  const [maxMusicSizeMB, setMaxMusicSizeMB] = useState(10); // e.g., 10 MB
  const [acceptedMusicFormats, setAcceptedMusicFormats] = useState(".mp3,.m4a");
  const [enableExtendedTimeFee, setEnableExtendedTimeFee] = useState(false);
  const [extendedTimeCost, setExtendedTimeCost] = useState(20); // e.g., $20 surcharge
  const [photoUploadsEnabled, setPhotoUploadsEnabled] = useState(true);
  const [videoUploadsEnabled, setVideoUploadsEnabled] = useState(true);
  const [maxPhotoCount, setMaxPhotoCount] = useState(3); // e.g., 3 photos per routine
  const [critiqueRecordingEnabled, setCritiqueRecordingEnabled] = useState(false);
  const [remoteJudgingEnabled, setRemoteJudgingEnabled] = useState(false);

  const handleSave = () => {
    // In a real scenario, you'd save to your back-end or state manager.
    console.log("Media settings saved:", {
      maxMusicSizeMB,
      acceptedMusicFormats,
      enableExtendedTimeFee,
      extendedTimeCost,
      photoUploadsEnabled,
      videoUploadsEnabled,
      maxPhotoCount,
      critiqueRecordingEnabled,
      remoteJudgingEnabled,
    });
    alert("Media settings have been saved (simulated)!");
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <h1 className="text-2xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
        Media & Upload Settings
      </h1>

      {/* Music Upload Config */}
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
        <h2 className="text-xl font-semibold mb-3 text-gray-800 dark:text-gray-100">
          Music Upload Configuration
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Max Music Size */}
          <div className="flex flex-col">
            <label
              htmlFor="maxMusicSizeMB"
              className="font-medium text-gray-700 dark:text-gray-300"
            >
              Max Music File Size (MB)
            </label>
            <input
              id="maxMusicSizeMB"
              className="mt-1 p-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 
                         dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              type="number"
              min={1}
              value={maxMusicSizeMB}
              onChange={(e) => setMaxMusicSizeMB(Number(e.target.value))}
            />
          </div>

          {/* Accepted Formats */}
          <div className="flex flex-col">
            <label
              htmlFor="acceptedMusicFormats"
              className="font-medium text-gray-700 dark:text-gray-300"
            >
              Accepted Music Formats
            </label>
            <input
              id="acceptedMusicFormats"
              className="mt-1 p-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 
                         dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              type="text"
              value={acceptedMusicFormats}
              onChange={(e) => setAcceptedMusicFormats(e.target.value)}
              placeholder=".mp3,.m4a"
            />
            <small className="text-gray-500 dark:text-gray-400 mt-1">
              Separate multiple formats with commas
            </small>
          </div>
        </div>
      </section>

      {/* Extended Time Fee Config */}
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
        <h2 className="text-xl font-semibold mb-3 text-gray-800 dark:text-gray-100">
          Extended Time Fee
        </h2>
        <div className="space-y-4">
          <div className="flex items-center">
            <input
              id="enableExtendedTimeFee"
              type="checkbox"
              className="mr-2 w-4 h-4 accent-blue-600"
              checked={enableExtendedTimeFee}
              onChange={(e) => setEnableExtendedTimeFee(e.target.checked)}
            />
            <label
              htmlFor="enableExtendedTimeFee"
              className="text-gray-700 dark:text-gray-300"
            >
              Enable surcharge for routines that exceed standard time limits
            </label>
          </div>

          {enableExtendedTimeFee && (
            <div className="flex items-center gap-3 ml-6">
              <label
                htmlFor="extendedTimeCost"
                className="font-medium text-gray-700 dark:text-gray-300"
              >
                Extended Time Cost ($)
              </label>
              <input
                id="extendedTimeCost"
                className="p-2 w-24 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 
                           dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                type="number"
                min={0}
                value={extendedTimeCost}
                onChange={(e) => setExtendedTimeCost(Number(e.target.value))}
              />
            </div>
          )}
        </div>
      </section>

      {/* Photo & Video Upload Config */}
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
        <h2 className="text-xl font-semibold mb-3 text-gray-800 dark:text-gray-100">
          Photo & Video Uploads
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Photo Upload Toggle */}
          <div className="flex items-center">
            <input
              id="photoUploadsEnabled"
              type="checkbox"
              className="mr-2 w-4 h-4 accent-blue-600"
              checked={photoUploadsEnabled}
              onChange={(e) => setPhotoUploadsEnabled(e.target.checked)}
            />
            <label
              htmlFor="photoUploadsEnabled"
              className="text-gray-700 dark:text-gray-300"
            >
              Enable Photo Upload (e.g., photogenic, modeling)
            </label>
          </div>

          {/* Video Upload Toggle */}
          <div className="flex items-center">
            <input
              id="videoUploadsEnabled"
              type="checkbox"
              className="mr-2 w-4 h-4 accent-blue-600"
              checked={videoUploadsEnabled}
              onChange={(e) => setVideoUploadsEnabled(e.target.checked)}
            />
            <label
              htmlFor="videoUploadsEnabled"
              className="text-gray-700 dark:text-gray-300"
            >
              Enable Video Upload (e.g., virtual routines)
            </label>
          </div>
        </div>

        {/* If Photo is enabled, show max photos input */}
        {photoUploadsEnabled && (
          <div className="flex flex-col mt-4">
            <label
              htmlFor="maxPhotoCount"
              className="font-medium text-gray-700 dark:text-gray-300"
            >
              Max Photos per Routine
            </label>
            <input
              id="maxPhotoCount"
              className="mt-1 p-2 w-32 rounded border border-gray-300 dark:border-gray-600 
                         dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 
                         focus:ring-blue-500"
              type="number"
              min={1}
              value={maxPhotoCount}
              onChange={(e) => setMaxPhotoCount(Number(e.target.value))}
            />
          </div>
        )}
      </section>

      {/* Critique & Remote Judging */}
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
        <h2 className="text-xl font-semibold mb-3 text-gray-800 dark:text-gray-100">
          Critique Recording & Remote Judging
        </h2>
        <div className="space-y-4">
          <div className="flex items-center">
            <input
              id="critiqueRecordingEnabled"
              type="checkbox"
              className="mr-2 w-4 h-4 accent-blue-600"
              checked={critiqueRecordingEnabled}
              onChange={(e) => setCritiqueRecordingEnabled(e.target.checked)}
            />
            <label
              htmlFor="critiqueRecordingEnabled"
              className="text-gray-700 dark:text-gray-300"
            >
              Enable Critique Recording (onsite or local capture)
            </label>
          </div>

          <div className="flex items-center">
            <input
              id="remoteJudgingEnabled"
              type="checkbox"
              className="mr-2 w-4 h-4 accent-blue-600"
              checked={remoteJudgingEnabled}
              onChange={(e) => setRemoteJudgingEnabled(e.target.checked)}
            />
            <label
              htmlFor="remoteJudgingEnabled"
              className="text-gray-700 dark:text-gray-300"
            >
              Enable Remote Judging (judges watch videos from home)
            </label>
          </div>
        </div>
      </section>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          className="inline-flex items-center px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white 
                     font-semibold rounded shadow transition-colors"
        >
          Save Settings
        </button>
      </div>
    </div>
  );
};

export default MediaSettings;
